import { authFetch } from '../utils/authFetch';

export type InvoiceCurrency = 'BBD' | 'USD';
export type InvoiceTerms = 'due_on_receipt' | 'fifty_fifty' | 'two_ten_net_thirty' | 'net_thirty';
export type InvoiceStatus = 'draft' | 'issued' | 'part_paid' | 'paid' | 'payment_exception' | 'voided';

export interface InvoiceLine {
  id?: string;
  source_type: 'catalog' | 'freeform';
  catalog_product_id?: string;
  sku?: string;
  description: string;
  quantity: number | string;
  unit_price: string;
  price_source?: 'variant' | 'item' | 'parent' | 'manual_bbd' | 'manual_usd' | null;
  price_override_reason?: string | null;
  fulfilment_type?: 'buy' | 'produce';
}

export interface InvoiceCustomer {
  name: string;
  company?: string;
  email: string;
  billing_address: string;
}

export interface InvoiceDraftInput {
  currency: InvoiceCurrency;
  terms: InvoiceTerms;
  customer: InvoiceCustomer;
  delivery: { method: 'ship' | 'collect'; shipping_address: string };
  discount: { type: 'amount'; amount: string };
  lines: InvoiceLine[];
}

export interface Invoice extends InvoiceDraftInput {
  id: string;
  number?: string | null;
  status: InvoiceStatus;
  version: number;
  issue_date?: string | null;
  due_date?: string | null;
  salesperson_name?: string | null;
  seller?: { name: string; descriptor?: string; address?: string; email?: string; tin?: string; footer?: string };
  email_status?: string | null;
  fulfilment_status?: string | null;
  fulfilment_error?: string | null;
  fulfilment_start_requested?: boolean;
  fulfillment_override?: { actor_id: string; actor_name?: string | null; reason: string; occurred_at: string } | null;
  paid_at?: string | null;
  completed_at?: string | null;
  void_reason?: string | null;
  voided_at?: string | null;
  adjustments?: { kind: string; base_delta: string; vat_delta: string; total_delta: string; reason: string; date: string }[];
  payments?: { id: string; source: string; installment: string | null; amount: string;
    currency: InvoiceCurrency; reference: string | null; verified_at: string;
    verified_by?: string | null; verified_by_name?: string | null;
    receipt_number?: string | null; balance_after_cents?: number | null }[];
  early_offer?: { deadline: string; base_discount: string; revised_vat: string; payable: string } | null;
  totals?: { subtotal: string; invoice_discount: string; taxable_base: string; vat: string; grand_total: string; deposit?: string | null; balance: string | null; current_balance: string; adjustments: string; paid: string };
}

export interface InvoiceListResult {
  items: InvoiceSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface InvoiceAvailabilityLine {
  invoice_line_id: string;
  catalog_product_id: string;
  description: string;
  requested_milli: number;
  on_hand_milli: number;
  reserved_milli: number;
  available_before_milli: number;
  reservable_milli: number;
  shortfall_milli: number;
}

export interface InvoiceAvailability { stock: InvoiceAvailabilityLine[]; has_shortage: boolean }
export interface InvoiceDeliveryAttempt {
  id: string; status: 'sending' | 'sent' | 'failed' | 'uncertain'; recipient_email: string;
  provider_message_id?: string | null; error_code?: string | null; request_key: string;
  created_at: string; completed_at?: string | null; resolved_at?: string | null;
  resolution_reference?: string | null; resolution_outcome?: string | null;
}

export interface ManualInvoicePaymentInput {
  method: 'cash' | 'card';
  mode: 'amount_due' | 'partial' | 'full';
  amount?: string;
  reference?: string;
  cash_received?: boolean;
  request_key: string;
  expected_balance: string;
  expected_payment_amount: string;
}

export interface ManualInvoicePaymentResult {
  payment_id: string;
  receipt_number: string;
  amount: string;
  currency: InvoiceCurrency;
  paid: string;
  balance: string;
  status: InvoiceStatus;
  reused: boolean;
}

export interface InvoiceSummary {
  id: string;
  number?: string | null;
  status: InvoiceStatus;
  currency: InvoiceCurrency;
  customer?: InvoiceCustomer;
  customer_name?: string;
  seller_snapshot?: { name: string } | null;
  issue_date?: string | null;
  due_date?: string | null;
  email_status?: string | null;
  fulfilment_status?: string | null;
  paid_at?: string | null;
  totals?: { grand_total: string; balance?: string | null; paid?: string };
}

export interface InvoiceCatalogProduct {
  id: string;
  sku: string;
  name: string;
  bbd_price: string | null;
  price_source?: 'variant' | 'item' | 'parent' | null;
  fulfilment_type: 'buy' | 'produce';
  on_hand?: number | null;
  available?: number | null;
}

export interface CreateInvoiceCatalogProduct {
  sku: string;
  name: string;
  bbd_price: string;
  fulfilment_type: 'buy' | 'produce';
  hidden_from_shop: true;
}

export class InvoiceRequestError extends Error {
  constructor(message: string, public readonly code: string | null, public readonly status: number) {
    super(message);
    this.name = 'InvoiceRequestError';
  }
}

async function json<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await authFetch(path, options);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new InvoiceRequestError(
      typeof body.message === 'string' ? body.message : `Invoice request failed (${response.status})`,
      typeof body.error === 'string' ? body.error : typeof body.code === 'string' ? body.code : null,
      response.status,
    );
  }
  return response.json() as Promise<T>;
}

// Keep transport mapping here as the server contract is finalized.
export const invoiceService = {
  list(params: URLSearchParams): Promise<InvoiceListResult> {
    return json(`/api/madebb/invoices?${params.toString()}`);
  },
  get(id: string): Promise<Invoice> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}`);
  },
  create(input: InvoiceDraftInput): Promise<Invoice> {
    return json('/api/madebb/invoices', { method: 'POST', body: JSON.stringify(input) });
  },
  update(id: string, version: number, input: InvoiceDraftInput): Promise<Invoice> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...input, version }),
    });
  },
  availability(id: string, version: number): Promise<InvoiceAvailability> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/availability`, {
      method: 'POST', body: JSON.stringify({ version }),
    });
  },
  issue(id: string, version: number, mode?: 'reserve_available' | 'no_inventory_effect',
    stock?: InvoiceAvailabilityLine[]): Promise<Invoice> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/issue`, {
      method: 'POST', body: JSON.stringify({ version, inventory_mode: mode, stock_snapshot: stock }),
    });
  },
  void(id: string, version: number, reason: string): Promise<Invoice> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/void`, {
      method: 'POST', body: JSON.stringify({ version, reason }),
    });
  },
  financeAccess(): Promise<{ can_record_usd: boolean }> {
    return json('/api/madebb/invoice-finance/access');
  },
  recordOfflineUsdPayment(id: string, input: { source: 'wire' | 'card_in_person'; amount: string;
    reference: string; evidence_reference: string }): Promise<{ status: InvoiceStatus; balance: string }> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/offline-payments`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  recordManualPayment(id: string, input: ManualInvoicePaymentInput): Promise<ManualInvoicePaymentResult> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/manual-payments`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  async receiptHtml(id: string, paymentId: string): Promise<string> {
    const response = await authFetch(`/api/madebb/invoices/${encodeURIComponent(id)}/manual-payments/${encodeURIComponent(paymentId)}/receipt`);
    if (!response.ok) throw new InvoiceRequestError('Receipt could not be opened', null, response.status);
    return response.text();
  },
  startFulfilment(id: string, reason: string): Promise<{ status: 'requested'; invoice_id: string; event_id: string;
    actor_id: string; reason: string; occurred_at: string; handoff_status: string }> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/start-fulfilment`, {
      method: 'POST', body: JSON.stringify({ reason }),
    });
  },
  sendEmail(id: string, idempotencyKey: string): Promise<InvoiceDeliveryAttempt> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/send`, {
      method: 'POST', body: JSON.stringify({ idempotency_key: idempotencyKey }),
    });
  },
  deliveryAttempts(id: string): Promise<InvoiceDeliveryAttempt[]> {
    return json<{ items: InvoiceDeliveryAttempt[] }>(`/api/madebb/invoices/${encodeURIComponent(id)}/delivery-attempts`)
      .then((result) => result.items);
  },
  resolveDeliveryAttempt(id: string, attemptId: string, input: {
    outcome: 'confirmed_sent' | 'confirmed_not_sent'; evidence_reference: string;
    provider_message_id?: string;
  }): Promise<InvoiceDeliveryAttempt> {
    return json(`/api/madebb/invoices/${encodeURIComponent(id)}/delivery-attempts/${encodeURIComponent(attemptId)}/resolve`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  catalog(search: string): Promise<{ items: InvoiceCatalogProduct[]; total: number; page_size: number }> {
    return json(`/api/madebb/invoice-catalog?q=${encodeURIComponent(search)}`);
  },
  createCatalogProduct(input: CreateInvoiceCatalogProduct): Promise<InvoiceCatalogProduct> {
    return json('/api/madebb/invoice-catalog', { method: 'POST', body: JSON.stringify(input) });
  },
};
